import { X } from 'lucide-react-native';
import type { Ref } from 'react';

import { REPLY_DRAFT_COPY } from '../copy';
import { EdgeFade } from '../shared/EdgeFade';
import type { HeadingNode } from '../shared/focusHeading';

import { GlassIconButton } from '@/components/ui/glass';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { View } from '@/components/ui/view';

/** Height of the modal's floating header: the title and the close button. */
const HEADER_OVERLAY_HEIGHT = 64;

/** How much of the body the modal's floating header covers. */
export function useHeaderOverlayHeight(): number {
  return HEADER_OVERLAY_HEIGHT;
}

/** How far the header's fade runs past the header before it clears. */
const HEADER_FADE_TAIL = 28;

type ReplyDraftHeaderProps = {
  title: string;
  onClose: () => void;
  titleRef?: Ref<HeadingNode>;
};

/**
 * The draft's header in the web modal: the view's title over a fade, so the
 * body passes out of sight beneath it, and the modals' round X on the right.
 */
export function ReplyDraftHeader({ title, onClose, titleRef }: ReplyDraftHeaderProps) {
  return (
    <View
      pointerEvents="box-none"
      className="absolute left-0 right-0 top-0 z-10"
      style={{ height: HEADER_OVERLAY_HEIGHT }}>
      <EdgeFade
        edge="top"
        height={HEADER_OVERLAY_HEIGHT + HEADER_FADE_TAIL}
        holdHeight={HEADER_OVERLAY_HEIGHT}
      />
      <HStack pointerEvents="box-none" className="h-full items-center gap-2 px-3">
        <View className="w-10" />
        <Heading
          ref={titleRef}
          size="h1-md"
          className="flex-1 text-center font-semibold text-typography-900">
          {title}
        </Heading>
        <GlassIconButton
          icon={X}
          accessibilityLabel={REPLY_DRAFT_COPY.closeLabel}
          onPress={onClose}
          fallbackClassName="bg-background-0 web:hover:bg-background-100"
        />
      </HStack>
    </View>
  );
}
